import { useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { SkeletonLines } from '../components/Skeleton';
import { ServerErrorState, SwitchedOffState } from '../components/system-states/SystemState';
import { useAppSettings } from '../lib/app-settings';
import { isRequirementOn, moduleForPath, requirementLabel } from '../lib/module-map';
import { useModules } from '../lib/modules';
import { useCanEdit } from '../stores/session';

/**
 * R1 (§L28): a screen that belongs to a switched-off module is never drawn. An old email, a
 * bookmark or a typed address lands on SYS-09 instead — in the content region only, so the shell
 * stays mounted (CLAUDE.md §5a rule 6).
 */
export function ModuleRouteGate({ children }: { readonly children: ReactNode }): ReactNode {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const settings = useAppSettings();
  const modules = useModules();
  const canConfigure = useCanEdit('admin_config');
  const module = moduleForPath(pathname);
  if (!module || isRequirementOn(module, modules)) return children;
  // D83: settings that never loaded are only the defaults, so they cannot say an admin switched this off.
  if (settings.data === undefined) {
    return settings.isError ? <ServerErrorState onRetry={() => void settings.refetch()} /> : <SkeletonLines count={6} />;
  }
  return <SwitchedOffState feature={requirementLabel(module)} canOpenSettings={canConfigure} />;
}
